import React, { useEffect, useState, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import { getInventoryNodes, deleteInventoryNode } from '../../actions/inventoryNode';
import { getServiceableAreas } from '../../actions/serviceableArea';
import { getSectors } from '../../actions/sector';
import { Plus, Search, Edit, Trash2, Package, Eye, AlertTriangle, Activity, Building, Monitor, Truck } from 'lucide-react';

const InventoryNodes = ({ 
    getInventoryNodes, 
    deleteInventoryNode, 
    getServiceableAreas,
    getSectors,
    inventoryNodes, 
    serviceableAreas,
    sectors,
    loading 
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [nodeToDelete, setNodeToDelete] = useState(null);
    const [filteredNodes, setFilteredNodes] = useState([]);
    const [statusFilter, setStatusFilter] = useState('all');
    const [typeFilter, setTypeFilter] = useState('all');
    const [serviceableAreaFilter, setServiceableAreaFilter] = useState('all');

    const nodeTypes = ['WAREHOUSE', 'PLANT', 'MICRO_WAREHOUSE', 'VENDING_MACHINE', 'VEHICLE'];

    useEffect(() => {
        document.title = 'Inventory Nodes - Management';
        getInventoryNodes();
        getServiceableAreas();
        getSectors();
    }, [getInventoryNodes, getServiceableAreas, getSectors]);

    useEffect(() => {
        if (inventoryNodes) {
            let filtered = inventoryNodes.filter(node =>
                node.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                node.type?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                node.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                node.contactDetails?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                node.sector?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                node.serviceableArea?.name?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            if (statusFilter !== 'all') {
                filtered = filtered.filter(node =>
                    statusFilter === 'active' ? node.isActive : !node.isActive
                );
            }

            if (typeFilter !== 'all') {
                filtered = filtered.filter(node => node.type === typeFilter);
            }

            if (serviceableAreaFilter !== 'all') {
                filtered = filtered.filter(node =>
                    node.serviceableArea?._id === serviceableAreaFilter
                );
            }

            setFilteredNodes(filtered);
        }
    }, [inventoryNodes, searchTerm, statusFilter, typeFilter, serviceableAreaFilter]);

    const handleDeleteClick = (node) => {
        setNodeToDelete(node);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (nodeToDelete) {
            deleteInventoryNode(nodeToDelete._id);
            setShowDeleteModal(false);
            setNodeToDelete(null);
        }
    };

    const cancelDelete = () => {
        setShowDeleteModal(false);
        setNodeToDelete(null);
    };

    const getStatusColor = (isActive) => {
        return isActive ? 'success' : 'secondary';
    };

    const getStatusText = (isActive) => {
        return isActive ? 'Active' : 'Inactive';
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'WAREHOUSE':
                return Building;
            case 'PLANT':
                return Package;
            case 'MICRO_WAREHOUSE':
                return Building;
            case 'VENDING_MACHINE':
                return Monitor;
            case 'VEHICLE':
                return Truck;
            default:
                return Package;
        }
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'WAREHOUSE':
                return 'primary';
            case 'PLANT':
                return 'success';
            case 'MICRO_WAREHOUSE':
                return 'info';
            case 'VENDING_MACHINE':
                return 'warning';
            case 'VEHICLE':
                return 'danger';
            default:
                return 'secondary';
        }
    };

    const formatInventoryLevel = (current, capacity) => {
        if (!capacity) return 'N/A';
        const percentage = Math.round((current / capacity) * 100);
        return `${current}/${capacity} (${percentage}%)`;
    };

    const getInventoryLevelColor = (current, capacity) => {
        if (!capacity) return 'secondary';
        const percentage = (current / capacity) * 100;
        if (percentage >= 80) return 'success';
        if (percentage >= 50) return 'warning';
        return 'danger';
    };

    if (loading && !inventoryNodes) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3 text-muted">Loading inventory nodes...</p>
                </div>
            </div>
        );
    }

    return (
        <Fragment>
            <div className="container-fluid py-4">
                {/* Header */}
                <div className="row mb-4">
                    <div className="col-12">
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="text-primary mb-1 fw-bold">
                                    <Package className="me-2" size={32} />
                                    Inventory Nodes
                                </h2>
                                <p className="text-muted mb-0">Manage warehouses, plants, micro warehouses, and other inventory locations</p>
                            </div>
                            <Link
                                to="/inventory/v1.2/inventory-nodes/new"
                                className="btn btn-primary btn-lg d-flex align-items-center"
                            >
                                <Plus size={20} className="me-2" />
                                Add Inventory Node
                            </Link>
                        </div>
                    </div>
                </div>

                {/* Filters */}
                <div className="row mb-4">
                    <div className="col-md-4">
                        <div className="input-group">
                            <span className="input-group-text bg-light">
                                <Search size={18} />
                            </span>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Search by name, type, address, or contact..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="col-md-2">
                        <select
                            className="form-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">All Status</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                        >
                            <option value="all">All Types</option>
                            {nodeTypes.map(type => (
                                <option key={type} value={type}>
                                    {type.replace('_', ' ')}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-md-3">
                        <select
                            className="form-select"
                            value={serviceableAreaFilter}
                            onChange={(e) => setServiceableAreaFilter(e.target.value)}
                        >
                            <option value="all">All Serviceable Areas</option>
                            {serviceableAreas?.map(area => (
                                <option key={area._id} value={area._id}>
                                    {area.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Stats Cards */}
                <div className="row mb-4">
                    <div className="col-md-3">
                        <div className="card bg-primary text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Total Nodes</h5>
                                        <h3 className="mt-2 mb-0">{inventoryNodes?.length || 0}</h3>
                                    </div>
                                    <Package size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-success text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Active Nodes</h5>
                                        <h3 className="mt-2 mb-0">
                                            {inventoryNodes?.filter(node => node.isActive).length || 0}
                                        </h3>
                                    </div>
                                    <Activity size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-warning text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Warehouses</h5>
                                        <h3 className="mt-2 mb-0">
                                            {inventoryNodes?.filter(node => 
                                                node.type === 'WAREHOUSE' || node.type === 'MICRO_WAREHOUSE'
                                            ).length || 0}
                                        </h3>
                                    </div>
                                    <Building size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-3">
                        <div className="card bg-info text-white">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h5 className="card-title mb-0">Filtered Results</h5>
                                        <h3 className="mt-2 mb-0">{filteredNodes?.length || 0}</h3>
                                    </div>
                                    <Search size={40} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="card shadow-sm">
                    <div className="card-header bg-light">
                        <h5 className="mb-0">Inventory Nodes List</h5>
                    </div>
                    <div className="card-body p-0">
                        {filteredNodes && filteredNodes.length > 0 ? (
                            <div className="table-responsive">
                                <table className="table table-hover mb-0">
                                    <thead className="bg-light">
                                        <tr>
                                            <th>Node Details</th>
                                            <th>Type</th>
                                            <th>Location</th>
                                            <th>Inventory Level</th>
                                            <th>Contact</th>
                                            <th>Status</th>
                                            <th className="text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredNodes.map((node) => {
                                            const TypeIcon = getTypeIcon(node.type);
                                            const totalCurrent = (node.currentInventory?.filledCans || 0) + 
                                                               (node.currentInventory?.emptyCans || 0);
                                            const totalCapacity = (node.capacity?.filledCans || 0) + 
                                                                 (node.capacity?.emptyCans || 0);
                                            
                                            return (
                                                <tr key={node._id}>
                                                    <td>
                                                        <div>
                                                            <div className="fw-bold text-primary">{node.name}</div>
                                                            <small className="text-muted">
                                                                {node.address.length > 50 
                                                                    ? `${node.address.substring(0, 50)}...`
                                                                    : node.address
                                                                }
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <TypeIcon size={16} className={`text-${getTypeColor(node.type)} me-2`} />
                                                            <span className={`badge bg-${getTypeColor(node.type)}`}>
                                                                {node.type.replace('_', ' ')}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            {node.serviceableArea && (
                                                                <div className="fw-semibold">
                                                                    {node.serviceableArea.name}
                                                                </div>
                                                            )}
                                                            {node.sector && (
                                                                <small className="text-muted">
                                                                    Sector: {node.sector.name}
                                                                </small>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <span className={`badge bg-${getInventoryLevelColor(totalCurrent, totalCapacity)} me-2`}>
                                                                {formatInventoryLevel(totalCurrent, totalCapacity)}
                                                            </span>
                                                        </div>
                                                        {node.currentInventory && (
                                                            <small className="text-muted d-block">
                                                                F: {node.currentInventory.filledCans || 0} | 
                                                                E: {node.currentInventory.emptyCans || 0} | 
                                                                D: {node.currentInventory.damagedCans || 0}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {node.contactDetails?.name ? (
                                                            <div>
                                                                <div className="fw-semibold">{node.contactDetails.name}</div>
                                                                {node.contactDetails.phone && (
                                                                    <small className="text-muted">{node.contactDetails.phone}</small>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <em className="text-muted">No contact</em>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`badge bg-${getStatusColor(node.isActive)}`}>
                                                            {getStatusText(node.isActive)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex justify-content-center gap-1">
                                                            <Link
                                                                to={`/inventory/v1.2/inventory-nodes/${node._id}`}
                                                                className="btn btn-outline-info btn-sm"
                                                                title="View Details"
                                                            >
                                                                <Eye size={14} />
                                                            </Link>
                                                            <button
                                                                onClick={() => handleDeleteClick(node)}
                                                                className="btn btn-outline-danger btn-sm"
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="text-center py-5">
                                <Package size={64} className="text-muted mb-3" />
                                <h5 className="text-muted">No inventory nodes found</h5>
                                <p className="text-muted mb-4">
                                    {searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || serviceableAreaFilter !== 'all'
                                        ? 'Try adjusting your search filters'
                                        : 'Create your first inventory node to get started'
                                    }
                                </p>
                                {(!searchTerm && statusFilter === 'all' && typeFilter === 'all' && serviceableAreaFilter === 'all') && (
                                    <Link
                                        to="/inventory/v1.2/inventory-nodes/new"
                                        className="btn btn-primary"
                                    >
                                        <Plus size={16} className="me-2" />
                                        Add First Inventory Node
                                    </Link>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header border-0">
                                <h5 className="modal-title">
                                    <AlertTriangle size={20} className="text-danger me-2" />
                                    Confirm Deletion
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={cancelDelete}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-0">
                                    Are you sure you want to delete the inventory node{' '}
                                    <strong>"{nodeToDelete?.name}"</strong>?
                                </p>
                                <div className="alert alert-warning mt-3 mb-0">
                                    <small>
                                        <strong>Warning:</strong> This action cannot be undone and may affect 
                                        associated inventory operations and transactions.
                                    </small>
                                </div>
                            </div>
                            <div className="modal-footer border-0">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={cancelDelete}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={confirmDelete}
                                >
                                    <Trash2 size={16} className="me-2" />
                                    Delete Node
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
};

InventoryNodes.propTypes = {
    getInventoryNodes: PropTypes.func.isRequired,
    deleteInventoryNode: PropTypes.func.isRequired,
    getServiceableAreas: PropTypes.func.isRequired,
    getSectors: PropTypes.func.isRequired,
    inventoryNodes: PropTypes.array,
    serviceableAreas: PropTypes.array,
    sectors: PropTypes.array,
    loading: PropTypes.bool.isRequired
};

const mapStateToProps = (state) => ({
    inventoryNodes: state.inventoryNode.inventoryNodes,
    serviceableAreas: state.serviceableArea.serviceableAreas,
    sectors: state.sector.sectors,
    loading: state.inventoryNode.loading
});

export default connect(mapStateToProps, {
    getInventoryNodes,
    deleteInventoryNode,
    getServiceableAreas,
    getSectors
})(InventoryNodes);
